import type { SearchTerm, SummarizerLabel } from "@/utils/summarizer-config"

interface Replacement {
  from: string
  to: string
  wholeWord?: boolean
}

const oneWayLexemeSynonyms: Replacement[] = [
  { from: "any risk", to: "investment risk" },
  { from: "any risk", to: "any more risk" },
  { from: "of having", to: "of not having" },
  { from: "you like", to: "you don't like" },
  { from: "cash sum", to: "lump sum" },
  { from: "cash sum", to: "capital sum" },
  { from: "cash sum", to: "capital lump sum" },
  { from: "cash sum", to: "cash lump sum" },
]

const oneWayWordSynonyms: Replacement[] = [
  { from: "accept", to: "take" },
  { from: "some", to: "a little" },
  { from: "some", to: "a minimal amount of" },
  { from: "some", to: "a limited amount of" },
  { from: "some", to: "a small amount of" },
  { from: "some", to: "an amount of" },
  { from: "some", to: "any" },
  { from: "willing", to: "unwilling" },
  { from: "you're", to: "you are" },
  { from: "tax", to: "taxation" },
  { from: "ATR", to: "Attitude to Risk" },
  { from: "OMO", to: "Open Market Option" },
  { from: "GMP", to: "Guaranteed Minimum Pension" },
  { from: "GAR", to: "Guaranteed Annuity Rate" },
  { from: "UFPLS", to: "Uncrystallised Fund Pension Lump Sum" },
  { from: "sum", to: "amount" },
  { from: "can't", to: "can not" },
  { from: "don't", to: "do not" },
  { from: "you've", to: "you have" },
  { from: "there's", to: "there is" },
  { from: "she", to: "he" },
  { from: "her", to: "his" },
]

const commonHyphenations = ["tax-free", "day-to-day", "cash-sum", "lump-sum", "cash-in"]
const commonOcrErrors: Replacement[] = [
  { from: "ri", to: "n", wholeWord: false },
  { from: "Mar", to: "Mai", wholeWord: true },
  { from: "e", to: "©", wholeWord: false },
  { from: "l", to: "i", wholeWord: false },
  { from: "l", to: "1", wholeWord: false },
  { from: "I", to: "1", wholeWord: true },
]

export function expandSearchTerm(searchTerm: string): string {
  const expandedTerm: { term: string; wholeWord?: boolean } = { term: searchTerm }
  let expression: RegExp

  expandedTerm.term = expandedTerm.term.replace(/[[\]{}()*+?.,\\^$|#]/g, "[\\-\\s\\n\\r]*\\$&[\\-\\s\\n\\r]*")
  expandedTerm.term = `\\b${expandedTerm.term}\\b`

  for (const synonym of oneWayLexemeSynonyms) {
    expression = new RegExp("\\b" + synonym.from + "\\b", "gi")
    expandedTerm.term = expandedTerm.term.replace(expression, `(${synonym.from}|${synonym.to})`)
  }

  for (const hyphenation of commonHyphenations) {
    expression = new RegExp(`\\b${hyphenation.replace("-", " ")}|${hyphenation.replace(" ", "-")}\\b`, "gi")
    expandedTerm.term = expandedTerm.term.replace(expression, hyphenation.replace("-", "[\\-\\s\\n\\r]+"))
  }

  for (const synonym of oneWayWordSynonyms) {
    expression = new RegExp("\\b" + synonym.from + "\\b", "gi")
    expandedTerm.term = expandedTerm.term.replace(expression, `(${synonym.from}|${synonym.to})`)
  }

  for (const ocrError of commonOcrErrors) {
    expression = new RegExp(
      (ocrError.wholeWord === true ? "\\b" : "") + ocrError.from + (expandedTerm.wholeWord === true ? "\\b" : ""),
      "gi",
    )
    expandedTerm.term = expandedTerm.term.replace(
      expression,
      `(${ocrError.wholeWord === true ? "\\b" : ""}${ocrError.from}|${ocrError.to}${ocrError.wholeWord === true ? "\\b" : ""})`,
    )
  }

  expression = new RegExp("\\s+", "g")
  expandedTerm.term = expandedTerm.term.replace(expression, "[\\s\\n\\r]+")

  return expandedTerm.term
}

export function createViewerSearchPatterns(terms: SearchTerm[]) {
  return terms.map(({ term, isRegex }) => ({ pattern: isRegex ? term : expandSearchTerm(term) }))
}

export function shouldAllowSearch(label: SummarizerLabel, classification: string, subClassification: string) {
  const isSubClassExcluded = label.documentSubClasses[classification]?.[subClassification] === "Do Not Check"
  const isGroupExcluded = label.documentClasses[classification] === "Do Not Check"
  return !isSubClassExcluded || !isGroupExcluded
}

const nonDocumentFileTypes = new Set(["ac5", "archive", "audio", "form"])

export function getSkipReason(file: {
  edxProcessingStatus?: string | null
  edxRecordProcessingClass?: string
  fileType?: string
  passwordStatus?: string
}) {
  if (file.edxProcessingStatus === "issue") return "Processing issue"
  const passwordStatus = file.passwordStatus?.trim() ?? ""
  if (passwordStatus !== "" && !/nopassword|success/i.test(passwordStatus)) return "Password protected"
  if (nonDocumentFileTypes.has(file.fileType?.toLowerCase() ?? "")) {
    return "Unsupported file type"
  }
  return null
}
